import React from 'react';
import { BRAND_NAME } from '../brand';
import { isChunkLoadError, notifyUpdateRequired } from '../utils/updateCheck';

/**
 * Top-level error boundary. Without this, any uncaught render error (e.g.
 * the VehicleDetail TDZ crash) unmounts the whole React tree to a blank
 * white screen with no indication of what happened. This catches the error,
 * keeps it in the console for debugging, and shows a recoverable fallback.
 *
 * Chunk-load failures get their own branch: a stale tab lazy-loading a chunk
 * the new deploy no longer serves isn't a bug, it's a version skew — so it
 * routes into the shared updateAvailable state (status 'required') and the
 * fallback says a refresh is needed, not "something went wrong". No
 * "Back to dashboard" there: navigating just lazy-loads another dead chunk.
 *
 * Error boundaries must be class components (no hook equivalent for
 * componentDidCatch / getDerivedStateFromError).
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error)) {
      // Same state the proactive /version.json poll feeds — the UpdateToast
      // (mounted outside this boundary) shows its required-refresh variant.
      notifyUpdateRequired();
      return;
    }
    // Keep the full error + component stack in the console. No logging
    // service is wired up yet; this is the hook point if one is added.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught a render error:', error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.isChunkError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-muted p-6">
          <div
            data-testid="chunk-update-fallback"
            className="max-w-md w-full bg-card border border-border rounded-lg p-6 text-center shadow-sm"
          >
            <h1 className="text-lg font-bold text-foreground">Update required</h1>
            <p className="text-sm text-muted-foreground mt-2">
              This tab is running an old version of {BRAND_NAME} and needs a refresh to load the
              latest update. Nothing is lost — refresh to continue.
            </p>
            <div className="flex items-center justify-center mt-5">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-muted p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-6 text-center shadow-sm">
          <h1 className="text-lg font-bold text-foreground">Something went wrong</h1>
          <p className="text-sm text-muted-foreground mt-2">
            This page hit an unexpected error and couldn&apos;t render. Your data is safe — try reloading.
          </p>
          <div className="flex items-center justify-center gap-3 mt-5">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={() => window.location.assign('/dashboard')}
              className="px-4 py-2 rounded-md border border-input bg-background text-foreground text-sm font-medium hover:bg-accent"
            >
              Back to dashboard
            </button>
          </div>
          {this.state.error?.message && (
            <p className="text-[11px] text-muted-foreground mt-4 font-mono break-words">
              {this.state.error.message}
            </p>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
